import React, { useRef, useState } from 'react';
import { FileText, X } from 'lucide-react';
import type { GuideKnowledgeBasePayload } from '../api/materialContextApi';
import { extractGuideMaterialContext } from '../api/materialContextApi';

interface GuideMaterialUploadPanelProps {
  value: GuideKnowledgeBasePayload | null;
  onChange: (payload: GuideKnowledgeBasePayload | null) => void;
}

/** Contexto PDF/TXT compacto — no debe dominar el modal de creación. */
export function GuideMaterialUploadPanel({ value, onChange }: GuideMaterialUploadPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const payload = await extractGuideMaterialContext(file);
      onChange(payload);
    } catch (e) {
      onChange(null);
      setError(e instanceof Error ? e.message : 'No se pudo extraer el texto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="guide-context-strip">
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.txt,.md,text/plain,application/pdf"
        className="hidden"
        onChange={(e) => void handleFile(e.target.files?.[0] ?? null)}
      />

      {!value ? (
        <button
          type="button"
          className="guide-context-strip__btn"
          disabled={loading}
          onClick={() => inputRef.current?.click()}
        >
          <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{loading ? 'Extrayendo…' : 'Añadir contexto (PDF/TXT)'}</span>
        </button>
      ) : (
        <div className="guide-context-strip__filled">
          <FileText className="h-3.5 w-3.5 shrink-0 text-[var(--micelio-musgo,#2d3b32)]" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="guide-context-strip__name truncate">{value.fileName}</p>
            <p className="guide-context-strip__meta truncate">
              Contexto
              {value.charCount != null ? ` · ${value.charCount.toLocaleString('es-CL')} caracteres` : ''}
              {value.truncated ? ' · recortado' : ''}
            </p>
          </div>
          <button
            type="button"
            className="guide-context-strip__remove"
            aria-label="Quitar contexto"
            onClick={() => onChange(null)}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {error ? <p className="guide-context-strip__error">{error}</p> : null}
    </div>
  );
}
